import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

// Helpers
import { isObjectInList } from "@/Common/helpers/Arrays";

// Hooks
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import Button from "@/Layouts/components/Buttons/Button";
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";

// Components
import MetricItem from "./MetricItem";

// MUI
import { Collapse, Grid } from "@mui/material";
import { TransitionGroup } from "react-transition-group";

// Icons
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";

const useAvailableOptions = (options = [], selectedOptions = [], filterBy = "metricName") => {
	return options
		?.filter((option) => !selectedOptions.some((item) => Boolean(item?.[filterBy]) && item[filterBy] === option?.[filterBy]))
		.map((option) => ({ ...option }));
};

const includeCurrentMetric = (options, actual, filterBy = "metricName") => {
	const updated = [...options];
	if (Boolean(actual?.[filterBy]) && !isObjectInList(options, filterBy, actual?.[filterBy])) {
		updated.push({ [filterBy]: actual?.[filterBy] });
	}
	return updated;
};

const getTooltip = (item, options = [], filterBy = "metricName") => {
	if (!options?.length || !item?.[filterBy]) {
		return null;
	}
	return options.find((option) => option?.[filterBy] === item?.[filterBy])?.tooltip || null;
};

export default function RequiredMetrics(props) {
	const {
		options = [],
		totalOptions = 0,
		complianceLevels = [],
		optionsLevelsDisabled = null,
		metrics = [],
		useLevels = false,
		metricTitle = "Metric",
		requiredTitle = "Compliance Required",
		emptyError = false,
		loading = false,
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// States
	const [innerMetrics, setInnerMetrics] = useState([]);

	// Hooks
	const availableOptions = useAvailableOptions(options, innerMetrics || []);
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const metricsErrors = errors?.get("metrics");
	const levelsErrors = errors?.get("levels");
	const errorsMessages = getErrorsMessages();

	const handleAdd = () => {
		const updated = [...innerMetrics];
		const metric = {
			required: false,
			metricName: "",
		};

		if (complianceLevels.length) {
			metric["metricLevel"] = "Base";
		}

		updated.push(metric);
		propagate(updated);
	};

	const handleChangeRequired = (index, value) => {
		const updated = [...innerMetrics];
		updated[index]["required"] = value;
		propagate(updated);
	};

	const handleSelect = (index, selected) => {
		const updated = [...innerMetrics];
		const isDifferent = Boolean(updated?.[index]?.metricName !== selected?.metricName);
		if (isDifferent) {
			updated[index]["metricName"] = selected?.metricName || "";
			propagate(updated);
		}
	};

	const handleSelectLevel = (index, selected) => {
		const updated = [...innerMetrics];
		const isDifferent = Boolean(updated?.[index]?.metricLevel !== selected?.level);
		if (isDifferent) {
			updated[index]["metricLevel"] = selected?.level || "";
			propagate(updated);
		}
	};

	const handleRemove = (index) => {
		const updated = [...innerMetrics];
		if (index !== -1 && updated?.length) {
			updated.splice(index, 1);
			propagate(updated);
		}
	};

	const propagate = (updatedMetrics) => {
		setInnerMetrics(updatedMetrics);
		onActionUpdate?.(updatedMetrics);
	};

	useMemo(() => {
		if (innerMetrics) {
			validate([
				{
					definition: {
						useKey: "metrics",
						operators: {
							conditions: [
								{
									type: "SELF",
									on: "metricName",
									located: "SET",
									distinct: "",
								},
							],
						},
						errorsMap: ["metricName"],
						message: `Missing metrics`,
						validationType: "SIMPLE",
					},
					section: [...innerMetrics],
				},
				{
					definition: {
						useKey: "levels",
						operators: {
							conditions: [
								{
									type: "SELF",
									on: "metricLevel",
									located: "SET",
									excludes: {
										key: "metricName",
										distinct: "",
										map: optionsLevelsDisabled,
									},
									apply: Boolean(optionsLevelsDisabled?.size),
								},
							],
						},
						errorsMap: ["metricName", "metricLevel"],
						message: `Invalid levels`,
						validationType: "SIMPLE_CONDITIONAL",
					},
					section: innerMetrics || [],
				},
			]);
		}
	}, [innerMetrics, optionsLevelsDisabled, validate]);

	useMemo(() => {
		onActionValidation?.(isValid);
	}, [isValid, onActionValidation]);

	useEffect(() => {
		setInnerMetrics(metrics);
		return () => {
			setInnerMetrics([]);
		};
	}, [metrics]);

	const isAddDisabled = () => {
		const metricsOverOptions = innerMetrics.length >= totalOptions;
		return !availableOptions?.length || metricsOverOptions;
	};

	const headerMinHeight = requiredTitle?.length > 10 ? "50px" : "35px";

	return (
		<Grid container>
			<Grid item xs={12}>
				<TransitionGroup style={{ width: "100%" }}>
					{innerMetrics?.map((element, key) => {
						return (
							<Collapse component={Grid} item xs={12} key={`required_metrics_${metricTitle}_${key}`}>
								<MetricItem
									index={key}
									item={element}
									tooltip={getTooltip(element, options)}
									useLevels={useLevels}
									headerMinHeight={headerMinHeight}
									metricTitle={metricTitle}
									requiredTitle={requiredTitle}
									error={Boolean(metricsErrors?.[key]?.metricName)}
									levelError={Boolean(levelsErrors?.[key]?.metricLevel)}
									options={includeCurrentMetric(availableOptions, element)}
									optionsLevels={complianceLevels}
									optionsLevelsDisabled={optionsLevelsDisabled}
									loadingOptions={loading}
									readOnly={readOnly}
									onActionChangeRequired={handleChangeRequired}
									onActionRemove={handleRemove}
									onActionSelectLevel={handleSelectLevel}
									onActionSelectMetric={handleSelect}
								/>
							</Collapse>
						);
					})}
				</TransitionGroup>
			</Grid>

			{Boolean(emptyError && !innerMetrics?.length) && (
				<Grid
					container
					direction="column"
					rowSpacing={0.2}
					justifyContent={"center"}
					alignContent={"center"}
					alignItems="flex-start"
					mt={1}
				>
					<TierAlertError text={"Please add one or more metrics."} />
				</Grid>
			)}

			{Boolean(errorsMessages?.length) && (
				<Grid container direction="column" rowSpacing={0.2} alignItems="flex-start" mt={1}>
					{errorsMessages?.map((message, key) => (
						<TierAlertError key={`error_required_metrics_${message}_${key}`} text={message} />
					))}
				</Grid>
			)}

			<Grid item container justifyContent={"center"} mt={2} sx={{ maxWidth: "305px !important", marginLeft: "73px" }}>
				<Button
					variant="contained"
					text="Add Metric"
					classes="tier-btn add-tier-btn"
					bgColor={"var(--primary-color)"}
					endIcon={<AddCircleRounded />}
					disabled={readOnly || isAddDisabled()}
					loading={loading}
					onClick={handleAdd}
				/>
			</Grid>
		</Grid>
	);
}
RequiredMetrics.propTypes = {
	options: PropTypes.array,
	totalOptions: PropTypes.number,
	complianceLevels: PropTypes.array,
	optionsLevelsDisabled: PropTypes.instanceOf(Map),
	metrics: PropTypes.array,
	useLevels: PropTypes.bool,
	metricTitle: PropTypes.string,
	requiredTitle: PropTypes.string,
	emptyError: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
